import { useEffect, useRef, useState } from "react";

const steps = [
  {
    num: "01",
    icon: "ri-search-eye-line",
    title: "需求深度洞察",
    subtitle: "我们懂你的业务痛点",
    desc: "通过深度访谈与现场调研，全面了解企业现有IT架构、安全现状、业务痛点与合规要求，输出专业的现状评估报告。",
    tags: ["现场调研", "安全评估", "需求分析", "风险识别"],
    color: "#1a56db",
  },
  {
    num: "02",
    icon: "ri-draft-line",
    title: "定制化方案构建",
    subtitle: "精准的产品组合",
    desc: "基于需求分析结果，由资深架构师团队量身定制解决方案，提供详细的技术方案文档、产品选型建议与实施计划。",
    tags: ["方案设计", "产品选型", "架构规划", "成本优化"],
    color: "#0891b2",
  },
  {
    num: "03",
    icon: "ri-rocket-line",
    title: "核心价值交付",
    subtitle: "降本增效，业务连续",
    desc: "专业工程师团队负责项目实施，严格按照项目计划推进，确保系统稳定上线，交付可量化的业务价值与安全提升。",
    tags: ["工程实施", "系统集成", "测试验收", "上线交付"],
    color: "#0e7490",
  },
  {
    num: "04",
    icon: "ri-customer-service-2-line",
    title: "全周期服务保障",
    subtitle: "售后与技术支持",
    desc: "提供7×24小时技术支持、定期巡检、安全运营服务，建立长期合作关系，随着业务发展持续优化和升级方案。",
    tags: ["7×24h支持", "定期巡检", "安全运营", "持续优化"],
    color: "#1a56db",
  },
];

export default function SolutionsProcess() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.2 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="py-20 lg:py-28 bg-[#f9fafb]" ref={ref}>
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#1a56db]/8 border border-[#1a56db]/20 rounded-full mb-4">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-flow-chart text-[#1a56db] text-xs"></i>
            </div>
            <span className="text-[#1a56db] text-xs font-medium">标准服务流程</span>
          </div>
          <h2 className="text-3xl lg:text-4xl font-bold text-[#0d1f3c] mb-3">
            专业方法论
            <span className="text-[#1a56db]"> 驱动每一个项目</span>
          </h2>
          <p className="text-gray-500 text-sm max-w-xl mx-auto leading-relaxed">
            无论是信息安全、基础架构还是弱电智能化，我们都遵循同一套经过验证的专业服务流程
          </p>
        </div>

        {/* Steps - horizontal timeline */}
        <div className="relative">
          {/* Connecting line */}
          <div className="hidden lg:block absolute top-16 left-0 right-0 h-px bg-gray-200 z-0">
            <div
              className="h-full bg-gradient-to-r from-[#1a56db] via-[#0891b2] to-[#0e7490] transition-all duration-1500 ease-out"
              style={{ width: visible ? "100%" : "0%" }}
            ></div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 lg:gap-8 relative z-10">
            {steps.map((step, idx) => (
              <div
                key={step.num}
                className="flex flex-col items-center lg:items-start text-center lg:text-left transition-all duration-700"
                style={{
                  opacity: visible ? 1 : 0,
                  transform: visible ? "translateY(0)" : "translateY(30px)",
                  transitionDelay: `${idx * 150}ms`,
                }}
              >
                {/* Step number circle */}
                <div
                  className="w-12 h-12 flex items-center justify-center rounded-full border-2 mb-6 relative z-10 bg-white flex-shrink-0"
                  style={{ borderColor: step.color }}
                >
                  <span className="text-sm font-bold" style={{ color: step.color }}>
                    {step.num}
                  </span>
                </div>

                {/* Card */}
                <div className="bg-white border border-gray-100 rounded-2xl p-6 w-full hover:border-gray-200 transition-all duration-300">
                  <div
                    className="w-10 h-10 flex items-center justify-center rounded-xl mb-4"
                    style={{ backgroundColor: `${step.color}12` }}
                  >
                    <i className={`${step.icon} text-xl`} style={{ color: step.color }}></i>
                  </div>
                  <h4 className="text-base font-bold text-[#0d1f3c] mb-1">{step.title}</h4>
                  <p className="text-xs mb-3" style={{ color: step.color }}>{step.subtitle}</p>
                  <p className="text-sm text-gray-500 leading-relaxed mb-4">{step.desc}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {step.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-2 py-0.5 text-xs rounded-full"
                        style={{ backgroundColor: `${step.color}10`, color: step.color }}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mt-16 pt-12 border-t border-gray-200">
          {[
            { value: "500+", label: "服务企业客户", icon: "ri-building-2-line" },
            { value: "10年+", label: "行业深耕经验", icon: "ri-time-line" },
            { value: "98%", label: "客户满意度", icon: "ri-star-line" },
            { value: "7×24h", label: "全天候技术支持", icon: "ri-customer-service-2-line" },
          ].map((stat) => (
            <div key={stat.label} className="text-center">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#1a56db]/8 mx-auto mb-3">
                <i className={`${stat.icon} text-[#1a56db] text-lg`}></i>
              </div>
              <div className="text-2xl lg:text-3xl font-bold text-[#0d1f3c] mb-1">{stat.value}</div>
              <div className="text-xs text-gray-400">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
